import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, CheckCircle, Clock, Shirt, ShieldCheck, AlertCircle, XCircle, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import axios from 'axios';

const MONTHS = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'];

function MonthPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => parseInt(value.split('-')[0]));
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedYear = parseInt(value.split('-')[0]);
  const selectedMonth = parseInt(value.split('-')[1]) - 1;

  const select = (m) => {
    onChange(`${year}-${String(m+1).padStart(2,'0')}`);
    setOpen(false);
  };

  const label = `${MONTHS[selectedMonth]} ${selectedYear}`;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setYear(selectedYear); setOpen(o => !o); }}
        className="h-8 px-3 text-xs rounded-full border-2 border-gray-200 bg-white/60 hover:border-isk-teal flex items-center gap-1.5 font-medium text-isk-text">
        <CalendarDays className="w-3.5 h-3.5 text-isk-teal" />
        {label}
      </button>
      {open && (
        <div className="absolute top-10 left-0 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 p-3 w-56">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setYear(y => y-1)} className="p-1 rounded-lg hover:bg-gray-100"><ChevronLeft className="w-4 h-4 text-isk-muted" /></button>
            <span className="text-sm font-bold text-isk-text">{year}</span>
            <button onClick={() => setYear(y => y+1)} className="p-1 rounded-lg hover:bg-gray-100"><ChevronRight className="w-4 h-4 text-isk-muted" /></button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {MONTHS.map((m, i) => {
              const isSelected = year === selectedYear && i === selectedMonth;
              return (
                <button key={i} onClick={() => select(i)}
                  className={`py-1.5 rounded-lg text-xs font-medium transition-colors ${isSelected ? 'bg-isk-teal text-white' : 'hover:bg-isk-teal/10 text-isk-text'}`}>
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => n != null && n !== '' ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n) : '-';
const fmtDur = (m) => { if (!m) return '-'; const h = Math.floor(m/60), min = m%60; return h > 0 ? `${h} jam ${min} mnt` : `${min} menit`; };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : '-';
const fmtCur = (n) => n ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n) : '-';

export default function Laundry({ userRole, userPropertyName }) {
  const isAdminRole = userRole === 'admin' || userRole === 'superadmin';
  const canVerifyPayment = userRole === 'superadmin';

  const [records, setRecords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roomsInfo, setRoomsInfo] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(isAdminRole ? '' : (userPropertyName || ''));
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('request');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Verifikasi pembayaran state
  const [proofs, setProofs] = useState([]);
  const [selectedProof, setSelectedProof] = useState(null);
  const [proofImageUrl, setProofImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [receivedDate, setReceivedDate] = useState('');

  // Modal catat pembayaran manual admin
  const [payTarget, setPayTarget] = useState(null); // { id, tagihan_id, tenant_name, amount, room_number }
  const [payForm, setPayForm] = useState({ tanggal_bayar: new Date().toISOString().slice(0,10), catatan: '', foto: null });
  const [submittingPay, setSubmittingPay] = useState(false);

  // Modal terima request state
  const [terimaTarget, setTerimaTarget] = useState(null);
  const [terimaForm, setTerimaForm] = useState({ kg: '', price_per_kg: '', is_subscription: false, tanggal_masuk: '', notes: '' });
  const [submittingTerima, setSubmittingTerima] = useState(false);

  // Input laundry form state
  const [form, setForm] = useState({
    property_name: isAdminRole ? '' : (userPropertyName || ''),
    room_number: '', kg: '', tanggal_masuk: new Date().toISOString().slice(0,10),
    is_subscription: false, price_per_kg: '', notes: '',
  });

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const totalHarga = !form.is_subscription && form.price_per_kg && form.kg
    ? parseFloat(form.price_per_kg) * parseFloat(form.kg) : 0;
  const totalTerimHarga = !terimaForm.is_subscription && terimaForm.price_per_kg && terimaForm.kg
    ? parseFloat(terimaForm.price_per_kg) * parseFloat(terimaForm.kg) : 0;

  const loadRoomsInfo = async (propName) => {
    if (!propName) { setRoomsInfo([]); return; }
    setLoadingRooms(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/staff/rooms-with-occupancy?property_name=${encodeURIComponent(propName)}`, cfg());
      setRoomsInfo(res.data || []);
    } catch { setRoomsInfo([]); } finally { setLoadingRooms(false); }
  };

  const fetchData = async () => {
    try {
      const propRes = await axios.get(`${BACKEND_URL}/api/staff/properties`, cfg());
      setProperties(propRes.data || []);
    } catch (e) { console.error('Properties error:', e.message); }
    try {
      const recRes = await axios.get(`${BACKEND_URL}/api/laundry`, cfg());
      setRecords(recRes.data || []);
    } catch (e) { console.error('Laundry records error:', e.message); }
    setLoading(false);
  };

  const fetchProofs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/payment-proofs`, cfg());
      setProofs(res.data.filter(p => p.bill?.bill_type === 'laundry'));
    } catch {}
  };

  const openProof = (proof) => {
    setSelectedProof(proof);
    setReceivedDate('');
    setProofImageUrl(null);
    setLoadingImage(true);
    axios.get(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(proof.id)}/image`, cfg())
      .then(r => setProofImageUrl(r.data.proof_image_url || null))
      .catch(() => setProofImageUrl(null))
      .finally(() => setLoadingImage(false));
  };

  const handleVerify = async (proofId, action) => {
    if (!canVerifyPayment) return;
    if (action === 'approve' && !receivedDate) { setError('Masukkan tanggal uang diterima'); return; }
    if (action === 'reject' && !window.confirm('Tolak pembayaran ini?')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(proofId)}/verify?action=${action}`,
        { received_date: receivedDate || null }, cfg());
      setSuccess(action === 'approve' ? 'Pembayaran disetujui!' : 'Pembayaran ditolak.');
      setSelectedProof(null);
      fetchProofs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal'); }
  };

  useEffect(() => {
    fetchData();
    fetchProofs();
    if (!isAdminRole && userPropertyName) loadRoomsInfo(userPropertyName);
  }, []);

  // Input laundry manual
  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/laundry`, {
        ...form, total_price: totalHarga || null, amount: form.is_subscription ? 0 : totalHarga,
      }, cfg());
      setSuccess('Laundry berhasil dicatat!');
      setShowForm(false);
      setForm({ property_name: isAdminRole ? '' : (userPropertyName||''), room_number:'', kg:'', tanggal_masuk: new Date().toISOString().slice(0,10), is_subscription:false, price_per_kg:'', notes:'' });
      if (isAdminRole) { setSelectedProperty(''); setRoomsInfo([]); }
      setSubTab('proses');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyimpan'); }
  };

  const handleSelesai = async (id) => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/laundry/${id}/selesai`, {}, cfg());
      setSuccess(data.message || 'Laundry selesai!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch {}
  };

  // Terima request tenant
  const openTerima = (r) => {
    setTerimaTarget(r);
    setTerimaForm({ kg: '', price_per_kg: '', is_subscription: false, tanggal_masuk: new Date().toISOString().slice(0,10), notes: r.notes || '' });
    setError('');
  };

  const handleTerima = async () => {
    setSubmittingTerima(true);
    setError('');
    try {
      await axios.put(`${BACKEND_URL}/api/laundry/${terimaTarget.id}/proses`, terimaForm, cfg());
      setSuccess('Request diterima — laundry mulai diproses!');
      setTerimaTarget(null);
      setSubTab('proses');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal'); }
    finally { setSubmittingTerima(false); }
  };

  const handleTolak = async (id) => {
    if (!window.confirm('Tolak request laundry ini? Tenant akan melihat status "Ditolak".')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/laundry/${id}/tolak`, {}, cfg());
      setSuccess('Request ditolak.');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal'); }
  };

  const requested = records.filter(r => r.status === 'requested');
  const proses = records.filter(r => r.status === 'proses');
  const selesai = records.filter(r => r.status === 'selesai');
  const pendingPayments = selesai.filter(r => r.tagihan_id && r.bill_status !== 'paid' && r.proof_status === 'pending_verification').length;

  // Rekap state
  const nowDate = new Date();
  const [rekapMonth, setRekapMonth] = useState(`${nowDate.getFullYear()}-${String(nowDate.getMonth()+1).padStart(2,'0')}`);
  const [rekapProperty, setRekapProperty] = useState('');
  const [rekapRoom, setRekapRoom] = useState('');
  const [rekapRecords, setRekapRecords] = useState([]);
  const [loadingRekap, setLoadingRekap] = useState(false);

  const fetchRekap = async () => {
    setLoadingRekap(true);
    try {
      const [year, month] = rekapMonth.split('-');
      const dateFrom = `${year}-${month}-01`;
      const dateTo = `${year}-${month}-${new Date(year, month, 0).getDate()}`;
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const { data } = await axios.get(`${BACKEND_URL}/api/laundry?${params}`, cfg());
      setRekapRecords(data.filter(r => r.status === 'selesai'));
    } catch (e) { console.error(e); }
    finally { setLoadingRekap(false); }
  };

  const [rekapDetail, setRekapDetail] = useState(null); // { property_name, room_number, name }
  const [rekapProofModal, setRekapProofModal] = useState(null); // { url, loading }

  const openRekapProof = (proofId) => {
    setRekapProofModal({ url: null, loading: true });
    axios.get(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(proofId)}/image`, cfg())
      .then(r => setRekapProofModal({ url: r.data.proof_image_url || null, loading: false }))
      .catch(() => setRekapProofModal({ url: null, loading: false }));
  };

  useEffect(() => { if (subTab === 'rekap') { fetchRekap(); setRekapDetail(null); } }, [subTab, rekapMonth]);

  const rekapFiltered = rekapRecords
    .filter(r => !rekapProperty || r.property_name === rekapProperty)
    .filter(r => !rekapRoom || r.room_number === rekapRoom);

  // Aggregate per property + room
  const rekapAgg = Object.values(
    rekapFiltered.reduce((acc, r) => {
      const key = `${r.property_name}|||${r.room_number}`;
      if (!acc[key]) acc[key] = { property_name: r.property_name, room_number: r.room_number, name: r.tenant_name || '-', total_kg: 0, total_amount: 0 };
      acc[key].total_kg += parseFloat(r.kg) || 0;
      acc[key].total_amount += parseFloat(r.bill_total || r.total_price || r.amount) || 0;
      return acc;
    }, {})
  ).sort((a, b) => a.property_name.localeCompare(b.property_name) || a.room_number.localeCompare(b.room_number));

  const rekapTotalKg = rekapAgg.reduce((s, r) => s + r.total_kg, 0);
  const rekapTotalAmount = rekapAgg.reduce((s, r) => s + r.total_amount, 0);

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
          <Shirt className="w-5 h-5 text-isk-teal" /> Laundry
          {userPropertyName && <span className="text-xs font-normal text-isk-muted">· {userPropertyName}</span>}
        </h2>
        <Button onClick={() => setShowForm(true)} size="sm" className="bg-isk-teal text-white rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Input Laundry
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-white/40 rounded-xl p-1 w-fit flex-wrap">
        <button onClick={() => setSubTab('request')}
          className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${subTab==='request' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          <AlertCircle className="w-4 h-4 text-yellow-500" /> Request
          {requested.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center">{requested.length}</span>
          )}
        </button>
        <button onClick={() => setSubTab('proses')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${subTab==='proses' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          <Clock className="w-4 h-4 text-orange-500" /> Proses ({proses.length})
        </button>
        <button onClick={() => setSubTab('selesai')}
          className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${subTab==='selesai' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          <CheckCircle className="w-4 h-4 text-green-500" /> Selesai ({selesai.length})
          {pendingPayments > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{pendingPayments}</span>}
        </button>
        <button onClick={() => setSubTab('rekap')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${subTab==='rekap' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          📊 Rekap
        </button>
      </div>

      {/* Sub-tab Request */}
      {subTab === 'request' && (
        <div className="space-y-3">
          {requested.length === 0 ? (
            <div className="text-center py-12 text-isk-muted">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Tidak ada request laundry dari tenant</p>
            </div>
          ) : requested.map(r => (
            <div key={r.id} className="glass-card rounded-2xl p-4 border-l-4 border-yellow-400">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-bold text-isk-text">{r.property_name}</span>
                    <span className="px-2 py-0.5 bg-isk-teal/10 text-isk-teal rounded-full text-xs font-medium">Kamar {r.room_number}</span>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Request Pickup</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 mb-2">
                    {r.pickup_date && (
                      <p className="text-xs text-isk-muted"><span className="font-medium text-isk-text">Pickup:</span> {fmtDate(r.pickup_date)}{r.pickup_time ? ` · ${r.pickup_time}` : ''}</p>
                    )}
                    {r.jenis_barang && (
                      <p className="text-xs text-isk-muted"><span className="font-medium text-isk-text">Jenis:</span> {r.jenis_barang}</p>
                    )}
                    {r.jenis_layanan && (
                      <p className="text-xs text-isk-muted"><span className="font-medium text-isk-text">Layanan:</span> {r.jenis_layanan}</p>
                    )}
                    {r.perkiraan_kg && (
                      <p className="text-xs text-isk-muted"><span className="font-medium text-isk-text">Est. berat:</span> ~{r.perkiraan_kg} kg</p>
                    )}
                    <p className="text-xs text-isk-muted"><span className="font-medium text-isk-text">Oleh:</span> {r.created_by_name || r.created_by}</p>
                  </div>
                  {r.notes && <p className="text-xs text-isk-muted italic bg-yellow-50 px-2 py-1.5 rounded-lg">"{r.notes}"</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0 min-w-[90px]">
                  <Button size="sm" onClick={() => openTerima(r)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs px-4 w-full">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Terima
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleTolak(r.id)}
                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-full text-xs px-4 w-full">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Tolak
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tab Proses */}
      {subTab === 'proses' && (
        <div className="space-y-3">
          {proses.length === 0 && <div className="text-center py-12 text-isk-muted">Tidak ada laundry yang sedang diproses</div>}
          {proses.map(r => (
            <div key={r.id} className="glass-card rounded-2xl p-4 border-l-4 border-orange-400">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-isk-text">{r.property_name}</span>
                    <span className="px-2 py-0.5 bg-isk-teal/10 text-isk-teal rounded-full text-xs font-medium">Kamar {r.room_number}</span>
                    {r.tenant_name && (
                      <span className="text-xs text-isk-muted font-medium">{r.tenant_name.trim().split(' ')[0]}</span>
                    )}
                    {!!r.is_subscription && <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">Langganan</span>}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                    <div className="p-2 rounded-lg bg-white/50">
                      <p className="text-isk-muted">Jumlah</p>
                      <p className="font-semibold text-isk-text">{r.kg} kg</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white/50">
                      <p className="text-isk-muted">Tgl Masuk</p>
                      <p className="font-semibold text-isk-text">{fmtDate(r.tanggal_masuk)}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white/50">
                      <p className="text-isk-muted">Mulai Proses</p>
                      <p className="font-semibold text-isk-text">{fmtDate(r.start_time)}</p>
                    </div>
                    {!r.is_subscription && (
                      <div className="p-2 rounded-lg bg-white/50">
                        <p className="text-isk-muted">Total Harga</p>
                        <p className="font-semibold text-isk-teal">{fmt(r.total_price || r.amount)}</p>
                      </div>
                    )}
                  </div>
                  {r.notes && <p className="text-xs text-isk-muted mt-2 italic">"{r.notes}"</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button size="sm" onClick={() => handleSelesai(r.id)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs px-4">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Selesai
                  </Button>
                  {userRole === 'superadmin' && (
                    <button onClick={async () => {
                      if (!window.confirm('Hapus data laundry ini?')) return;
                      try { await axios.delete(`${BACKEND_URL}/api/laundry/${r.id}`, cfg()); fetchData(); }
                      catch { setError('Gagal menghapus'); }
                    }} className="flex items-center justify-center gap-1 text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded-full hover:bg-red-50 transition-colors">
                      <X className="w-3 h-3" /> Hapus
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tab Selesai */}
      {subTab === 'selesai' && (
        <div className="glass-card rounded-2xl overflow-hidden">
          {selesai.length === 0 && <div className="text-center py-12 text-isk-muted">Belum ada yang selesai</div>}
          {selesai.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-white/60">
                    <th className="text-left px-3 py-2.5 text-isk-muted font-medium">Kamar</th>
                    <th className="text-left px-3 py-2.5 text-isk-muted font-medium">Tenant</th>
                    <th className="text-left px-3 py-2.5 text-isk-muted font-medium">Tgl Masuk</th>
                    <th className="text-left px-3 py-2.5 text-isk-muted font-medium">Selesai</th>
                    <th className="text-right px-3 py-2.5 text-isk-muted font-medium">Kg</th>
                    <th className="text-right px-3 py-2.5 text-isk-muted font-medium">Total</th>
                    <th className="text-center px-3 py-2.5 text-isk-muted font-medium">Status</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {selesai.map(r => {
            const proof = proofs.find(p => p.bill_id === r.tagihan_id);
            const isPaid = r.bill_status === 'paid';
            const hasProof = !!proof;
            return (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-white/40 transition-colors">
                      <td className="px-3 py-2.5">
                        <span className="font-medium text-isk-text">{r.room_number}</span>
                      </td>
                      <td className="px-3 py-2.5 text-isk-text font-medium">
                        {r.tenant_name ? r.tenant_name.trim().split(' ').slice(0,2).join(' ') : (r.created_by_name || '-')}
                      </td>
                      <td className="px-3 py-2.5 text-isk-muted">{fmtDate(r.tanggal_masuk)}</td>
                      <td className="px-3 py-2.5 text-isk-muted">{fmtDate(r.end_time)}</td>
                      <td className="px-3 py-2.5 text-right text-isk-text">{r.kg} kg</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-isk-teal">
                        {r.tagihan_id ? fmtCur(r.bill_amount) : (!!r.is_subscription ? '-' : fmt(r.total_price || r.amount))}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {!r.tagihan_id ? (
                          <span className="text-isk-muted">-</span>
                        ) : isPaid ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Lunas</span>
                        ) : hasProof ? (
                          <button onClick={() => openProof(proof)} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium hover:bg-blue-200 transition-colors">
                            Bukti ↗
                          </button>
                        ) : (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">Belum bayar</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          {!isPaid && r.tagihan_id && canVerifyPayment && (
                            <button onClick={() => { setPayTarget({ id: r.id, tagihan_id: r.tagihan_id, tenant_name: r.tenant_name, amount: r.bill_amount, room_number: r.room_number }); setPayForm({ metode_bayar: 'tunai', tanggal_bayar: new Date().toISOString().slice(0,10), catatan: '', foto: null }); }}
                              className="px-2 py-1 bg-isk-teal hover:bg-isk-teal/80 text-white rounded-full text-[10px] font-medium transition-colors whitespace-nowrap">
                              Catat Bayar
                            </button>
                          )}
                          {userRole === 'superadmin' && (
                            <button onClick={async () => {
                              if (!window.confirm('Hapus data laundry ini?')) return;
                              try { await axios.delete(`${BACKEND_URL}/api/laundry/${r.id}`, cfg()); fetchData(); }
                              catch { setError('Gagal menghapus'); }
                            }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors">
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
            );
          })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Catat Pembayaran Manual Admin */}
      {payTarget && canVerifyPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !submittingPay && setPayTarget(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-isk-text text-sm">Catat Pembayaran</h3>
                <p className="text-xs text-isk-muted mt-0.5">Kamar {payTarget.room_number} · {payTarget.tenant_name || '-'}</p>
              </div>
              <button onClick={() => setPayTarget(null)} disabled={submittingPay}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>

            <div className="mb-3 p-2.5 rounded-xl bg-isk-teal/5 border border-isk-teal/20 flex justify-between items-center">
              <span className="text-xs text-isk-muted">Total Tagihan</span>
              <span className="font-bold text-isk-teal text-sm">{payTarget.amount ? new Intl.NumberFormat('id-ID',{style:'currency',currency:'IDR',minimumFractionDigits:0}).format(payTarget.amount) : '-'}</span>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-isk-text">Tanggal Bayar</label>
                <input type="date" value={payForm.tanggal_bayar} onChange={e => setPayForm(f=>({...f,tanggal_bayar:e.target.value}))}
                  className="mt-1 w-full h-9 px-3 text-xs rounded-xl border-2 border-gray-200 focus:outline-none focus:border-isk-teal" />
              </div>

              <div>
                <label className="text-xs font-medium text-isk-text">Foto Bukti Pembayaran <span className="text-red-500">*</span></label>
                <input type="file" accept="image/*" required onChange={e => setPayForm(f=>({...f,foto:e.target.files[0]||null}))}
                  className={`mt-1 w-full text-xs text-isk-muted file:mr-2 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:bg-isk-teal/10 file:text-isk-teal file:cursor-pointer rounded-xl border-2 py-1 ${!payForm.foto ? 'border-gray-200' : 'border-green-400'}`} />
                {!payForm.foto && <p className="text-[10px] text-isk-muted mt-0.5">Wajib upload foto struk, nota, atau bukti transfer</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-isk-text">Catatan <span className="text-isk-muted font-normal">(opsional)</span></label>
                <input type="text" value={payForm.catatan} onChange={e => setPayForm(f=>({...f,catatan:e.target.value}))}
                  placeholder="cth: bayar di kantor" maxLength={100}
                  className="mt-1 w-full h-9 px-3 text-xs rounded-xl border-2 border-gray-200 focus:outline-none focus:border-isk-teal" />
              </div>
            </div>

            <button disabled={submittingPay || !payForm.foto} onClick={async () => {
              if (!canVerifyPayment) return;
              if (!payForm.foto) { setError('Foto bukti pembayaran wajib diupload'); return; }
              setSubmittingPay(true); setError('');
              try {
                const fd = new FormData();
                fd.append('metode_bayar', 'transfer');
                fd.append('tanggal_bayar', payForm.tanggal_bayar);
                if (payForm.catatan) fd.append('catatan', payForm.catatan);
                fd.append('foto', payForm.foto);
                await axios.post(`${BACKEND_URL}/api/admin/laundry/${payTarget.id}/catat-pembayaran`, fd,
                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } });
                setPayTarget(null);
                setSuccess('Pembayaran berhasil dicatat dan masuk riwayat transaksi');
                fetchData(); fetchProofs();
                setTimeout(() => setSuccess(''), 4000);
              } catch(e) { setError(e.response?.data?.detail || 'Gagal mencatat pembayaran'); }
              finally { setSubmittingPay(false); }
            }} className="mt-4 w-full py-2.5 bg-isk-teal hover:bg-isk-teal/80 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {submittingPay ? 'Menyimpan...' : 'Simpan Pembayaran'}
            </button>
          </div>
        </div>
      )}

      {/* Sub-tab Rekap */}
      {subTab === 'rekap' && (
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap items-center">
            <MonthPicker value={rekapMonth} onChange={setRekapMonth} />
            {isAdminRole && (
              <select value={rekapProperty} onChange={e => { setRekapProperty(e.target.value); setRekapRoom(''); }}
                className="h-8 px-3 text-xs rounded-full border-2 border-gray-200 bg-white/60 focus:outline-none focus:border-isk-teal">
                <option value="">Semua Gedung</option>
                {properties.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            )}
            <input type="text" placeholder="Cari kamar..." value={rekapRoom} onChange={e => setRekapRoom(e.target.value)}
              className="h-8 px-3 text-xs rounded-full border-2 border-gray-200 bg-white/60 focus:outline-none focus:border-isk-teal w-32" />
          </div>

          {/* Table */}
          <div className="glass-card rounded-2xl overflow-auto max-h-[60vh]">
            {loadingRekap ? (
              <div className="py-12 text-center text-isk-muted text-sm">Memuat...</div>
            ) : rekapAgg.length === 0 ? (
              <div className="py-12 text-center text-isk-muted text-sm">Tidak ada data laundry selesai untuk periode ini</div>
            ) : (
              <table className="w-full min-w-max text-xs">
                <thead>
                  <tr className="bg-white/60 border-b border-gray-100 sticky top-0 z-10">
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Gedung</th>
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Kamar</th>
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Nama</th>
                    <th className="px-3 py-2.5 text-right font-medium text-isk-muted">Total KG</th>
                    <th className="px-3 py-2.5 text-right font-medium text-isk-muted">Total Tagihan</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rekapAgg.map((r, i) => {
                    const isSelected = rekapDetail?.property_name === r.property_name && rekapDetail?.room_number === r.room_number;
                    return (
                      <tr key={i} className={`border-b border-gray-50 transition-colors cursor-pointer ${isSelected ? 'bg-isk-teal/10' : 'hover:bg-white/40'}`}
                        onClick={() => setRekapDetail(isSelected ? null : r)}>
                        <td className="px-3 py-2.5 text-isk-muted whitespace-nowrap">{r.property_name}</td>
                        <td className="px-3 py-2.5 font-medium text-isk-text whitespace-nowrap">{r.room_number}</td>
                        <td className="px-3 py-2.5 text-isk-text whitespace-nowrap">{r.name}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-isk-text">{r.total_kg.toFixed(1)} kg</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-isk-teal">{fmtCur(r.total_amount)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? 'rotate-90 text-isk-teal' : 'text-isk-muted'}`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-white/60 border-t-2 border-gray-200 font-bold">
                    <td className="px-3 py-2.5 text-isk-text" colSpan={3}>Total ({rekapAgg.length} kamar)</td>
                    <td className="px-3 py-2.5 text-right text-isk-text">{rekapTotalKg.toFixed(1)} kg</td>
                    <td className="px-3 py-2.5 text-right text-isk-teal">{fmtCur(rekapTotalAmount)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Detail Panel */}
          {rekapDetail && (() => {
            const detailRecords = rekapFiltered.filter(r => r.property_name === rekapDetail.property_name && r.room_number === rekapDetail.room_number);
            const payBadge = (r) => {
              if (r.bill_status === 'paid') return <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Lunas</span>;
              if (r.proof_status === 'pending_verification') return <span className="px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Menunggu</span>;
              if (r.tagihan_id) return <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Belum Bayar</span>;
              return <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium">-</span>;
            };
            return (
              <div className="glass-card rounded-2xl overflow-hidden border border-isk-teal/30">
                <div className="px-4 py-3 bg-isk-teal/10 border-b border-isk-teal/20 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-isk-text">{rekapDetail.name} — Kamar {rekapDetail.room_number}</p>
                    <p className="text-xs text-isk-muted">{rekapDetail.property_name} · {detailRecords.length} transaksi</p>
                  </div>
                  <button onClick={() => setRekapDetail(null)}><X className="w-4 h-4 text-isk-muted hover:text-isk-text" /></button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max text-xs">
                    <thead>
                      <tr className="bg-white/60 border-b border-gray-100">
                        <th className="px-3 py-2 text-left font-medium text-isk-muted">Tgl Masuk</th>
                        <th className="px-3 py-2 text-left font-medium text-isk-muted">Selesai</th>
                        <th className="px-3 py-2 text-right font-medium text-isk-muted">KG</th>
                        <th className="px-3 py-2 text-right font-medium text-isk-muted">Tagihan</th>
                        <th className="px-3 py-2 text-center font-medium text-isk-muted">Status</th>
                        <th className="px-3 py-2 text-center font-medium text-isk-muted">Bukti</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRecords.map((r, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-white/40">
                          <td className="px-3 py-2 text-isk-muted whitespace-nowrap">{fmtDate(r.tanggal_masuk)}</td>
                          <td className="px-3 py-2 text-isk-muted whitespace-nowrap">{fmtDate(r.end_time)}</td>
                          <td className="px-3 py-2 text-right text-isk-text">{parseFloat(r.kg).toFixed(1)} kg</td>
                          <td className="px-3 py-2 text-right font-semibold text-isk-teal">{fmtCur(r.bill_total || r.total_price || r.amount)}</td>
                          <td className="px-3 py-2 text-center">{payBadge(r)}</td>
                          <td className="px-3 py-2 text-center">
                            {r.proof_id ? (
                              <button onClick={() => openRekapProof(r.proof_id)}
                                className="text-isk-teal hover:text-isk-teal/70 underline underline-offset-2">
                                Lihat
                              </button>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Rekap Proof Image Modal */}
          {rekapProofModal && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setRekapProofModal(null)}>
              <div className="bg-white rounded-2xl p-4 max-w-sm w-full max-h-[90vh] flex flex-col gap-3" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-isk-text">Bukti Pembayaran</p>
                  <button onClick={() => setRekapProofModal(null)}><X className="w-4 h-4 text-isk-muted" /></button>
                </div>
                {rekapProofModal.loading ? (
                  <div className="flex items-center justify-center py-10 text-isk-muted text-sm">Memuat...</div>
                ) : rekapProofModal.url ? (
                  <img src={rekapProofModal.url} alt="Bukti" className="w-full max-h-[70vh] object-contain rounded-xl border border-gray-200" />
                ) : (
                  <div className="flex items-center justify-center py-10 text-isk-muted text-sm">Foto tidak tersedia</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Terima Request */}
      {terimaTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="font-bold text-isk-text">Terima Request Laundry</h3>
                <p className="text-xs text-isk-muted mt-0.5">Lengkapi data untuk mulai memproses</p>
              </div>
              <button onClick={() => setTerimaTarget(null)}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>

            {/* Info tenant (read-only) */}
            <div className="mb-4 p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 flex items-center gap-3">
              <Shirt className="w-5 h-5 text-isk-teal shrink-0" />
              <div>
                <p className="text-sm font-semibold text-isk-text">{terimaTarget.property_name} · Kamar {terimaTarget.room_number}</p>
                <p className="text-xs text-isk-muted">Oleh: {terimaTarget.created_by_name || terimaTarget.created_by}</p>
                {terimaTarget.notes && <p className="text-xs text-isk-muted italic mt-0.5">"{terimaTarget.notes}"</p>}
              </div>
            </div>

            <div className="space-y-3">
              {/* Langganan toggle */}
              <div className={`rounded-xl border-2 overflow-hidden ${terimaForm.is_subscription ? 'border-blue-400' : 'border-gray-200'}`}>
                <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${terimaForm.is_subscription ? 'bg-blue-50' : 'bg-white/60'}`}>
                  <input type="checkbox" checked={terimaForm.is_subscription}
                    onChange={e => setTerimaForm(f => ({ ...f, is_subscription: e.target.checked, price_per_kg: '' }))}
                    className="accent-blue-500 w-4 h-4" />
                  <span className={`text-sm font-medium ${terimaForm.is_subscription ? 'text-blue-600' : 'text-isk-muted'}`}>Langganan</span>
                  {terimaForm.is_subscription && <span className="text-xs text-blue-500">· Tidak perlu isi harga</span>}
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Jumlah (kg) *</Label>
                  <Input type="number" step="0.1" min="0" value={terimaForm.kg}
                    onChange={e => setTerimaForm(f => ({ ...f, kg: e.target.value }))}
                    placeholder="0.0" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Tanggal Masuk *</Label>
                  <Input type="date" value={terimaForm.tanggal_masuk}
                    onChange={e => setTerimaForm(f => ({ ...f, tanggal_masuk: e.target.value }))}
                    className="mt-1 rounded-xl" />
                </div>
              </div>

              {!terimaForm.is_subscription && (
                <div>
                  <Label className="text-xs">Harga per kg (Rp) *</Label>
                  <Input type="number" min="0" value={terimaForm.price_per_kg}
                    onChange={e => setTerimaForm(f => ({ ...f, price_per_kg: e.target.value }))}
                    placeholder="0" className="mt-1 rounded-xl" />
                  {totalTerimHarga > 0 && (
                    <div className="mt-2 p-3 rounded-xl bg-isk-teal/10 flex justify-between items-center">
                      <span className="text-xs text-isk-muted">Total ({terimaForm.kg} kg × {fmt(terimaForm.price_per_kg)})</span>
                      <span className="font-bold text-isk-teal">{fmt(totalTerimHarga)}</span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs">Catatan</Label>
                <Input value={terimaForm.notes} onChange={e => setTerimaForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Opsional" className="mt-1 rounded-xl" />
              </div>

              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setTerimaTarget(null)} className="flex-1 rounded-full">Batal</Button>
                <Button onClick={handleTerima} disabled={submittingTerima} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full">
                  {submittingTerima ? 'Memproses...' : 'Terima & Proses'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail Verifikasi Pembayaran */}
      {selectedProof && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-isk-text">{selectedProof.tenant?.name}</h3>
                <p className="text-xs text-isk-muted">{selectedProof.tenant?.property_name} · Kamar {selectedProof.tenant?.room_number}</p>
              </div>
              <button onClick={() => setSelectedProof(null)}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>
            <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/10 text-xs mb-4 space-y-1">
              <p className="text-isk-muted">{selectedProof.bill?.other_description}</p>
              <p className="text-lg font-bold text-isk-teal">{fmtCur(selectedProof.bill?.total_amount)}</p>
              {selectedProof.notes && <p className="text-isk-muted italic">Catatan: {selectedProof.notes}</p>}
            </div>
            <div className="mb-4">
              <p className="text-xs font-semibold text-isk-muted mb-2">Bukti Transfer</p>
              {loadingImage ? (
                <div className="h-40 rounded-xl bg-gray-50 flex items-center justify-center text-isk-muted text-sm">Memuat foto...</div>
              ) : proofImageUrl ? (
                <img src={proofImageUrl} alt="Bukti" className="w-full max-h-64 object-contain rounded-xl border border-gray-200 cursor-zoom-in"
                  onClick={() => { const el = document.getElementById('lbImg'); if(el){ el.src=proofImageUrl; document.getElementById('lbOverlay').classList.remove('hidden'); } }} />
              ) : (
                <div className="h-32 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center text-red-500 text-sm">Foto tidak tersedia</div>
              )}
            </div>
            {selectedProof.status === 'pending_verification' && !canVerifyPayment ? (
              <p className="p-3 rounded-xl bg-yellow-50 text-yellow-700 text-sm text-center">Menunggu verifikasi Super Admin</p>
            ) : selectedProof.status === 'pending_verification' ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-isk-muted font-medium">Tanggal Uang Diterima *</label>
                  <input type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)}
                    className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-isk-teal/30" />
                </div>
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <div className="flex gap-2">
                  <Button onClick={() => handleVerify(selectedProof.id, 'reject')}
                    variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full text-sm">Tolak</Button>
                  <Button onClick={() => handleVerify(selectedProof.id, 'approve')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full text-sm">
                    <CheckCircle className="w-4 h-4 mr-1" /> Setujui
                  </Button>
                </div>
              </div>
            ) : (
              <div className={`p-3 rounded-xl text-sm text-center font-medium ${selectedProof.status === 'verified' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                {selectedProof.status === 'verified' ? '✓ Pembayaran sudah diverifikasi' : '✕ Pembayaran ditolak'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Input Laundry Manual */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">Input Laundry</h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              {!isAdminRole ? (
                <div className="p-2 rounded-lg bg-gray-50 text-xs">Gedung: <span className="font-semibold text-isk-text">{userPropertyName}</span></div>
              ) : (
                <div>
                  <Label className="text-xs">1. Pilih Properti *</Label>
                  <select value={selectedProperty}
                    onChange={e => { const v = e.target.value; setSelectedProperty(v); setForm(f => ({ ...f, property_name: v, room_number: '' })); setRoomsInfo([]); if (v) loadRoomsInfo(v); }}
                    className="mt-1 w-full h-9 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-isk-teal/40">
                    <option value="">-- Pilih gedung --</option>
                    {properties.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
              )}
              {(selectedProperty || !isAdminRole) && (() => {
                const occupiedRooms = roomsInfo.filter(r => r.status === 'occupied');
                const selectedRoom = occupiedRooms.find(r => r.room_number === form.room_number);
                return (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">{isAdminRole ? '2. ' : ''}Pilih Kamar *</Label>
                      {loadingRooms ? (
                        <div className="mt-1 h-9 rounded-xl border border-gray-200 bg-gray-50 flex items-center px-3 text-xs text-isk-muted">Memuat kamar...</div>
                      ) : (
                        <select value={form.room_number} onChange={e => setForm(f => ({ ...f, room_number: e.target.value }))}
                          className="mt-1 w-full h-9 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-isk-teal/40">
                          <option value="">-- Pilih kamar --</option>
                          {occupiedRooms.map(r => (
                            <option key={r.room_number} value={r.room_number}>
                              Kamar {r.room_number} — {r.tenant_name || 'Terisi'}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    {selectedRoom && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-isk-teal/10 border border-isk-teal/20">
                        <div className="w-7 h-7 rounded-full bg-isk-teal/20 flex items-center justify-center text-isk-teal text-xs font-bold shrink-0">
                          {(selectedRoom.tenant_name || '?').charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-isk-text">{selectedRoom.tenant_name || 'Tenant tidak diketahui'}</p>
                          <p className="text-xs text-isk-muted">Kamar {selectedRoom.room_number}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Jumlah (kg) *</Label>
                  <Input type="number" step="0.1" min="0" value={form.kg} onChange={e=>setForm(f=>({...f,kg:e.target.value}))} placeholder="0.0" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Tanggal Masuk *</Label>
                  <Input type="date" value={form.tanggal_masuk} onChange={e=>setForm(f=>({...f,tanggal_masuk:e.target.value}))} className="mt-1 rounded-xl" />
                </div>
              </div>
              <div className={`rounded-xl border-2 overflow-hidden ${form.is_subscription ? 'border-blue-400' : 'border-gray-200'}`}>
                <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${form.is_subscription ? 'bg-blue-50' : 'bg-white/60'}`}>
                  <input type="checkbox" checked={form.is_subscription}
                    onChange={e => setForm(f => ({...f, is_subscription: e.target.checked, price_per_kg: ''}))}
                    className="accent-blue-500 w-4 h-4" />
                  <span className={`text-sm font-medium ${form.is_subscription ? 'text-blue-600' : 'text-isk-muted'}`}>Langganan</span>
                  {form.is_subscription && <span className="text-xs text-blue-500">· Tidak perlu isi harga</span>}
                </label>
              </div>
              {!form.is_subscription && (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Harga per kg (Rp) *</Label>
                    <Input type="number" min="0" value={form.price_per_kg} onChange={e=>setForm(f=>({...f,price_per_kg:e.target.value}))} placeholder="0" className="mt-1 rounded-xl" />
                  </div>
                  {totalHarga > 0 && (
                    <div className="p-3 rounded-xl bg-isk-teal/10 flex justify-between items-center">
                      <span className="text-xs text-isk-muted">Total Harga ({form.kg} kg × {fmt(form.price_per_kg)})</span>
                      <span className="font-bold text-isk-teal">{fmt(totalHarga)}</span>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs">Catatan</Label>
                <Input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Opsional" className="mt-1 rounded-xl" />
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" className="flex-1 bg-isk-teal text-white rounded-full">Catat</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox foto bukti */}
      <div id="lbOverlay" className="hidden fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4"
        onClick={() => document.getElementById('lbOverlay').classList.add('hidden')}>
        <img id="lbImg" src="" alt="Bukti" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
      </div>
    </div>
  );
}
